import React, { useMemo, useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, {
  AccountSummary,
  DELIVERY_MODES,
  IDENTITY_KINDS,
  IdentitySummary,
  MEDIA_FORMATS,
  PLATFORMS,
  POLICY_TEMPLATES,
  SOURCE_POLICIES,
} from "../../../../../database/mediaOps/mediaOpsApi";
import { ianaTimeZones } from "../../../../../database/mediaOps/zonedTime";
import { useMutation, useOpRead } from "./hooks";
import { modeLabel } from "./stateLabels";
import {
  Banner,
  CheckLabel,
  FieldLabel,
  FormGrid,
  Grow,
  Hint,
  ListRow,
  Mono,
  Panel,
  PanelHead,
  ResultNotice,
  Row,
  Select,
  Small,
  TextArea,
  TextInput,
} from "./ui";

// Accounts and identities. Every action is shown to everyone. The server
// enforces privilege and the result shows FORBIDDEN plainly. The owner address
// is never shown in full: only the masked form the server returns.

const TEMPLATE_LABEL: Record<string, string> = {
  founderInstagram: "Founder Instagram (owner email only)",
  founderTiktok: "Founder TikTok (inbox or owner email)",
  influencer: "Influencer",
  brand: "Brand",
};

export const AccountsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const accounts = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const identities = useOpRead(() => mediaOps.listIdentities(communityId), [communityId]);
  const [openId, setOpenId] = useState<string | null>(null);
  const idList: IdentitySummary[] = identities.result?.data?.identities || [];
  const idName = (id: string) => idList.find((i) => i.id === id)?.name || id;
  const list: AccountSummary[] = accounts.result?.data?.accounts || [];
  const reloadAll = () => {
    accounts.reload();
    identities.reload();
  };

  return (
    <>
      <Panel>
        <PanelHead>
          Accounts <Grow />
          <Button size="sm" variant="secondary" onClick={reloadAll}>
            Refresh
          </Button>
        </PanelHead>
        <Hint>Each account is one destination. Targets are always chosen by account, never by platform.</Hint>
        {accounts.result && accounts.result.status === "blocked" ? <ResultNotice result={accounts.result} /> : null}
        {accounts.loading && !accounts.result ? <Small>Loading accounts.</Small> : null}
        {list.length === 0 && accounts.result && accounts.result.status !== "blocked" ? <Small>No accounts yet.</Small> : null}
        {list.map((a) => (
          <AccountRow
            key={a.id}
            communityId={communityId}
            account={a}
            identityName={idName(a.identityId)}
            open={openId === a.id}
            onToggle={() => setOpenId(openId === a.id ? null : a.id)}
            onChanged={accounts.reload}
          />
        ))}
      </Panel>
      <CreateAccountForm communityId={communityId} identities={idList} onCreated={accounts.reload} />
      <IdentitiesPanel communityId={communityId} identities={idList} onChanged={identities.reload} />
    </>
  );
};

const AccountRow: React.FC<{
  communityId: string;
  account: AccountSummary;
  identityName: string;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}> = ({ communityId, account: a, identityName, open, onToggle, onChanged }) => {
  const activation = useMutation();
  const activate = async () => {
    const res = await activation.run((k) => mediaOps.upsertAccount(k, communityId, {
      accountId: a.id, expectedVersion: a.version,
      platform: a.platform, handle: a.handle, identityId: a.identityId,
      timezone: a.timezone, status: "active",
      ...(a.providerAccountId ? { providerAccountId: a.providerAccountId } : {}),
      ...(a.connection ? { connection: a.connection } : {}),
    }));
    if (res && res.status !== "blocked") onChanged();
  };
  const conflicts = a.conflicts || [];
  const verification = a.verification?.status || "unverified";
  return (
    <ListRow $active={open} data-testid={`account-${a.id}`} style={{ flexDirection: "column", alignItems: "stretch" }}>
      <Row>
        <strong>@{a.handle}</strong>
        <Tag size="sm">{a.platform}</Tag>
        <Small>Identity: {identityName}</Small>
        <Tag size="sm" variant={a.status === "active" ? "success" : a.status === "conflict" ? "danger" : "warning"}>
          {a.status}
        </Tag>
        <Grow />
        <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={open}>
          {open ? "Close" : "Manage"}
        </Button>
      </Row>
      <Row>
        <Small>Modes:</Small>
        {(a.allowedModes || []).map((m) => (
          <Tag key={m} size="sm" variant={m === a.defaultMode ? "accent" : "neutral"}>
            {modeLabel(m)}
            {m === a.defaultMode ? " (default)" : ""}
          </Tag>
        ))}
        <Small>Policy v{a.policyVersion}</Small>
        <Small>Time zone {a.timezone}</Small>
      </Row>
      <Row>
        {a.ownerConfigured && a.ownerMasked ? (
          <Tag size="sm" variant="success">
            Owner: {a.ownerMasked}
          </Tag>
        ) : (
          <Tag size="sm" variant="danger">
            No owner set. Owner email targets are blocked.
          </Tag>
        )}
        <Tag size="sm" variant={verification === "verified" ? "success" : verification === "failed" ? "danger" : "warning"}>
          Verification: {verification}
        </Tag>
        {a.verification?.evidence ? <Small>Evidence: {a.verification.evidence}</Small> : null}
      </Row>
      {conflicts.length > 0 ? (
        <Banner $tone="danger">
          <strong>Destination conflict.</strong>
          <span>
            Another account claims the same provider destination. Delivery to this account is blocked until the conflict is resolved.
          </span>
          {conflicts.map((c, i) => (
            <Small key={i}>{c.claimedByOtherCommunity ? "Claimed by another community" : `Account ${c.accountId}`}</Small>
          ))}
        </Banner>
      ) : null}
      {open ? (
        <>
          {a.status === "disabled" ? <Panel>
            <PanelHead>Activate publishing</PanelHead>
            <Small>Allow reviewed posts to use this account’s permitted publishing methods. Your connected social account and review requirements stay the same.</Small>
            <Button size="sm" onClick={activate} isLoading={activation.busy} disabled={conflicts.length > 0}>Activate account</Button>
            {activation.result ? <ResultNotice result={activation.result} /> : null}
          </Panel> : null}
          <OwnerForm communityId={communityId} account={a} onChanged={onChanged} />
          <VerificationForm communityId={communityId} account={a} onChanged={onChanged} />
          <PolicyForm communityId={communityId} account={a} onChanged={onChanged} />
        </>
      ) : null}
    </ListRow>
  );
};

const OwnerForm: React.FC<{ communityId: string; account: AccountSummary; onChanged: () => void }> = ({ communityId, account, onChanged }) => {
  const [email, setEmail] = useState("");
  const m = useMutation<{ ownerMasked?: string; staleApprovalIds?: string[] }>();
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.setOwner(k, communityId, { accountId: account.id, expectedVersion: account.ownerVersion ?? 0, email: email.trim() })
    );
    if (res && !res.transportError) {
      // Never keep the full address on screen after the server has it.
      setEmail("");
      if (res.status !== "blocked") onChanged();
    }
  };
  return (
    <Panel>
      <PanelHead>Owner recipient</PanelHead>
      <Banner $tone="warning">
        Saving a new owner makes current approvals for this account stale. It sends nothing. The owner gets packs only for posts approved
        after this change.
      </Banner>
      <Row>
        <div style={{ flex: "1 1 240px" }}>
          <FieldLabel>
            Owner email
            <TextInput
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                m.edited();
              }}
            />
          </FieldLabel>
        </div>
        <Button size="sm" onClick={submit} disabled={!email.trim()} isLoading={m.busy}>
          Save owner
        </Button>
      </Row>
      <ResultNotice
        result={m.result}
        okText={m.result?.data?.ownerMasked ? `Owner set to ${m.result.data.ownerMasked}. Nothing was sent.` : "Owner saved. Nothing was sent."}
      />
      {m.result?.data?.staleApprovalIds && m.result.data.staleApprovalIds.length > 0 ? (
        <Small>{m.result.data.staleApprovalIds.length} approvals are now stale and need a new review.</Small>
      ) : null}
    </Panel>
  );
};

const VerificationForm: React.FC<{ communityId: string; account: AccountSummary; onChanged: () => void }> = ({
  communityId,
  account,
  onChanged,
}) => {
  const [status, setStatus] = useState<"verified" | "unverified">("verified");
  const [evidence, setEvidence] = useState("");
  const m = useMutation();
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.setVerification(k, communityId, { accountId: account.id, status, evidence: evidence.trim(), expectedVersion: account.version })
    );
    if (res && res.status !== "blocked") onChanged();
  };
  return (
    <Panel>
      <PanelHead>Verification</PanelHead>
      <Hint>Use a provider fact, such as the authenticated account id. A display name match is not evidence.</Hint>
      <FormGrid>
        <FieldLabel>
          Status
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as "verified" | "unverified");
              m.edited();
            }}
          >
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </Select>
        </FieldLabel>
        <FieldLabel>
          Evidence
          <TextInput
            value={evidence}
            onChange={(e) => {
              setEvidence(e.target.value);
              m.edited();
            }}
          />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={submit} disabled={evidence.trim().length < 3} isLoading={m.busy}>
          Save verification
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Verification saved." />
    </Panel>
  );
};

const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const sameSet = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((x) => b.includes(x));

const PolicyForm: React.FC<{ communityId: string; account: AccountSummary; onChanged: () => void }> = ({ communityId, account, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [allowedModes, setAllowedModes] = useState<string[]>(account.allowedModes || []);
  const [defaultMode, setDefaultMode] = useState<string>(account.defaultMode || "owner_email");
  const [prohibited, setProhibited] = useState<string[]>(account.prohibitedFormats || []);
  const [sourcePolicy, setSourcePolicy] = useState<string>(account.sourcePolicy || "founder_owned_only");
  const [reason, setReason] = useState("");
  const [confirmRelax, setConfirmRelax] = useState(false);
  const m = useMutation();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    m.edited();
  };

  const patch = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (!sameSet(allowedModes, account.allowedModes)) p.allowedModes = allowedModes;
    if (defaultMode !== account.defaultMode) p.defaultMode = defaultMode;
    if (!sameSet(prohibited, account.prohibitedFormats)) p.prohibitedFormats = prohibited;
    if (sourcePolicy !== account.sourcePolicy) p.sourcePolicy = sourcePolicy;
    return p;
  }, [allowedModes, defaultMode, prohibited, sourcePolicy, account]);

  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.updatePolicy(k, communityId, {
        accountId: account.id,
        expectedVersion: account.policyVersion,
        patch,
        reason: reason.trim(),
        ...(confirmRelax ? { confirmRelaxFounderRestrictions: true } : {}),
      })
    );
    if (res && res.status !== "blocked") onChanged();
  };

  if (!open) {
    return (
      <Row>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          Show advanced policy options
        </Button>
      </Row>
    );
  }
  return (
    <Panel>
      <PanelHead>
        Policy (advanced) <Grow />
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Hide
        </Button>
      </PanelHead>
      <Hint>Changing policy makes current approvals for this account stale. Founder restrictions stay unless a super admin confirms below.</Hint>
      <div>
        <Small>Allowed modes</Small>
        <Row>
          {DELIVERY_MODES.map((mode) => (
            <CheckLabel key={mode}>
              <input type="checkbox" checked={allowedModes.includes(mode)} onChange={() => edit(setAllowedModes)(toggle(allowedModes, mode))} />
              {modeLabel(mode)}
            </CheckLabel>
          ))}
        </Row>
      </div>
      <FormGrid>
        <FieldLabel>
          Default mode
          <Select value={defaultMode} onChange={(e) => edit(setDefaultMode)(e.target.value)}>
            {DELIVERY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {modeLabel(mode)}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Source policy
          <Select value={sourcePolicy} onChange={(e) => edit(setSourcePolicy)(e.target.value)}>
            {SOURCE_POLICIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      <div>
        <Small>Prohibited formats</Small>
        <Row>
          {MEDIA_FORMATS.map((f) => (
            <CheckLabel key={f}>
              <input type="checkbox" checked={prohibited.includes(f)} onChange={() => edit(setProhibited)(toggle(prohibited, f))} />
              {f}
            </CheckLabel>
          ))}
        </Row>
      </div>
      <FieldLabel>
        Reason (required)
        <TextInput value={reason} onChange={(e) => edit(setReason)(e.target.value)} />
      </FieldLabel>
      <CheckLabel>
        <input type="checkbox" checked={confirmRelax} onChange={(e) => edit(setConfirmRelax)(e.target.checked)} />
        I confirm this relaxes founder restrictions on purpose. The reason above is logged with my name.
      </CheckLabel>
      <Row>
        <Button size="sm" onClick={submit} disabled={!reason.trim() || Object.keys(patch).length === 0} isLoading={m.busy}>
          Save policy
        </Button>
        {Object.keys(patch).length === 0 ? <Small>No changes yet.</Small> : null}
      </Row>
      <ResultNotice result={m.result} okText="Policy saved. Approvals for this account need a new review." />
    </Panel>
  );
};

const CreateAccountForm: React.FC<{ communityId: string; identities: IdentitySummary[]; onCreated: () => void }> = ({
  communityId,
  identities,
  onCreated,
}) => {
  const zones = useMemo(() => ianaTimeZones(), []);
  const [platform, setPlatform] = useState<string>("instagram");
  const [handle, setHandle] = useState("");
  const [identityId, setIdentityId] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [template, setTemplate] = useState<string>("founderInstagram");
  const [more, setMore] = useState(false);
  const [providerAccountId, setProviderAccountId] = useState("");
  const m = useMutation();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    m.edited();
  };
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.upsertAccount(k, communityId, {
        platform,
        handle: handle.trim(),
        identityId,
        timezone,
        template,
        ...(providerAccountId.trim() ? { providerAccountId: providerAccountId.trim() } : {}),
      })
    );
    if (res && res.status !== "blocked") {
      setHandle("");
      setProviderAccountId("");
      onCreated();
    }
  };
  return (
    <Panel>
      <PanelHead>Add an account</PanelHead>
      <FormGrid>
        <FieldLabel>
          Platform
          <Select value={platform} onChange={(e) => edit(setPlatform)(e.target.value)}>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Handle
          <TextInput placeholder="@handle" value={handle} onChange={(e) => edit(setHandle)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Identity
          <Select value={identityId} onChange={(e) => edit(setIdentityId)(e.target.value)}>
            <option value="">Choose an identity</option>
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.kind})
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Policy template
          <Select value={template} onChange={(e) => edit(setTemplate)(e.target.value)}>
            {POLICY_TEMPLATES.map((t) => (
              <option key={t} value={t}>
                {TEMPLATE_LABEL[t]}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Time zone
          <Select value={timezone} onChange={(e) => edit(setTimezone)(e.target.value)}>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      {more ? (
        <FieldLabel>
          Provider account id (optional, required for TikTok inbox)
          <TextInput value={providerAccountId} onChange={(e) => edit(setProviderAccountId)(e.target.value)} />
        </FieldLabel>
      ) : (
        <Row>
          <Button size="sm" variant="ghost" onClick={() => setMore(true)}>
            More options
          </Button>
        </Row>
      )}
      <Row>
        <Button size="sm" onClick={submit} disabled={!handle.trim() || !identityId} isLoading={m.busy}>
          Save account
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Account saved." />
    </Panel>
  );
};

const IdentitiesPanel: React.FC<{ communityId: string; identities: IdentitySummary[]; onChanged: () => void }> = ({
  communityId,
  identities,
  onChanged,
}) => {
  const [kind, setKind] = useState<string>("founder");
  const [name, setName] = useState("");
  const [voice, setVoice] = useState("");
  const [audience, setAudience] = useState("");
  const m = useMutation();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    m.edited();
  };
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.upsertIdentity(k, communityId, {
        kind,
        name: name.trim(),
        ...(voice.trim() ? { voice: voice.trim() } : {}),
        ...(audience.trim() ? { audience: audience.trim() } : {}),
      })
    );
    if (res && res.status !== "blocked") {
      setName("");
      setVoice("");
      setAudience("");
      onChanged();
    }
  };
  return (
    <Panel>
      <PanelHead>Identities</PanelHead>
      <Hint>An identity is the person or brand the content is about. Founder identities accept founder-owned media only.</Hint>
      {identities.length === 0 ? <Small>No identities yet.</Small> : null}
      {identities.map((i) => (
        <ListRow key={i.id}>
          <strong>{i.name}</strong>
          <Tag size="sm">{i.kind}</Tag>
          <Mono>{i.id}</Mono>
          <Small>v{i.version}</Small>
        </ListRow>
      ))}
      <FormGrid>
        <FieldLabel>
          Kind
          <Select value={kind} onChange={(e) => edit(setKind)(e.target.value)}>
            {IDENTITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Name
          <TextInput value={name} onChange={(e) => edit(setName)(e.target.value)} />
        </FieldLabel>
      </FormGrid>
      <FormGrid>
        <FieldLabel>
          Voice (optional)
          <TextArea value={voice} onChange={(e) => edit(setVoice)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Audience (optional)
          <TextArea value={audience} onChange={(e) => edit(setAudience)(e.target.value)} />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={submit} disabled={!name.trim()} isLoading={m.busy}>
          Add identity
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Identity saved." />
    </Panel>
  );
};

export default AccountsView;
